import { ReactNode } from 'react';
import {
  Repeat,
  ShieldAlert,
  Loader2,
  Pause,
  Play,
  Ban,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  useSubscriptionDetailQuery,
  useSubscriptionListQuery,
} from '@/client/api/payment/payment.query';
import { useSubscriptionActionMutation } from '@/client/api/payment/payment.mutation';
import {
  formatMoney,
  Subscription,
  SubscriptionListParams,
} from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { PaymentsFilterBar, SortableTh } from '@/client/components/admin/Payment/PaymentsFilterBar';
import {
  EntityRelationshipLink,
  OrgRelationshipLink,
  RelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';

import { ExportCsvButton } from '@/client/components/admin/Payment/ExportCsvButton';
const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  past_due: 'bg-orange-100 text-orange-800',
  canceled: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-600',
  trialing: 'bg-blue-100 text-blue-800',
  payment_pending: 'bg-purple-100 text-purple-800',
};

const BILLING_STATUS_OPTIONS = (
  ['active', 'paused', 'past_due', 'canceled', 'expired', 'trialing', 'payment_pending'] as const
).map((s) => ({ value: s, label: s }));

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing subscriptions requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const SubscriptionDetailsDrawer = ({
  plid,
  isOpen,
  setIsOpen,
  canEdit,
  canVoid,
  onClose,
}: {
  plid: number | null;
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
  canEdit: boolean;
  canVoid: boolean;
  onClose: () => void;
}) => {
  const { data: sub, isLoading } = useSubscriptionDetailQuery(isOpen ? plid : null);
  const actionMutation = useSubscriptionActionMutation();

  if (!isOpen) return null;

  const status = String(sub?.billing_status || '');

  const run = async (
    action: 'cancel' | 'uncancel' | 'pause' | 'resume' | 'renew',
    label: string,
  ) => {
    if (!sub) return;
    try {
      await actionMutation.mutateAsync({ plid: sub.plid, action });
      toast.success(label, { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || `Failed to ${action}`, { richColors: true });
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <Repeat className="h-5 w-5 text-blue-600" />
            {isLoading ? 'Loading…' : `Subscription #${sub?.plid ?? plid}`}
          </DrawerTitle>
          <DrawerDescription>{sub?.interval_type || 'Subscription details'}</DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}
          {!isLoading && sub && (
            <>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                  STATUS_BADGE[status] || STATUS_BADGE.active
                }`}
              >
                {status.toUpperCase() || 'UNKNOWN'}
              </span>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-2xl font-black text-gray-900">
                  {formatMoney(sub.amount, sub.ccode)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="PLID" value={sub.plid} />
                <InfoRow label="Interval" value={sub.interval_type || '—'} />
                <InfoRow label="User" value={<UserRelationshipLink uid={sub.uid} summary={sub.user_summary} />} />
                <InfoRow label="Org" value={<OrgRelationshipLink oid={sub.oid} summary={sub.org_summary} />} />
                <InfoRow label="Entity" value={<EntityRelationshipLink eid={sub.eid} summary={sub.entity_summary} />} />
                <InfoRow
                  label="Card"
                  value={
                    sub.bpmid != null ? (
                      <RelationshipLink
                        kind="card"
                        id={sub.bpmid}
                        label={`bpmid ${sub.bpmid}`}
                        extra={{ uid: sub.uid ?? undefined, oid: sub.oid ?? undefined, bpmid: sub.bpmid }}
                      />
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow label="Expires" value={formatDate(sub.date_expired)} />
                <InfoRow
                  label="Cancel at period end"
                  value={sub.cancel_at_period_end === 1 || sub.cancel_at_period_end === true ? 'Yes' : 'No'}
                />
              </div>
            </>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canVoid && sub && status !== 'canceled' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={actionMutation.isPending}
                onClick={() => run('cancel', 'Subscription canceled')}
              >
                {actionMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="mr-1.5 h-4 w-4" />
                )}
                Cancel
              </Button>
            )}
            {canEdit && sub && status === 'canceled' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={actionMutation.isPending}
                onClick={() => run('uncancel', 'Subscription uncanceled')}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Uncancel
              </Button>
            )}
            {canEdit && sub && status === 'active' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={actionMutation.isPending}
                onClick={() => run('pause', 'Subscription paused')}
              >
                <Pause className="mr-1.5 h-4 w-4" />
                Pause
              </Button>
            )}
            {canEdit && sub && status === 'paused' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={actionMutation.isPending}
                onClick={() => run('resume', 'Subscription resumed')}
              >
                <Play className="mr-1.5 h-4 w-4" />
                Resume
              </Button>
            )}
            {canEdit && sub && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={actionMutation.isPending}
                onClick={() => run('renew', 'Renewal triggered')}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Renew
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const SubscriptionListPage = () => {
  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: 'Subscriptions' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const editPerm = useHasPermission('payment.edit');
  const voidPerm = useHasPermission('payment.void');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canEdit = editPerm.isSuperAdmin || editPerm.hasPermission;
  const canVoid = voidPerm.isSuperAdmin || voidPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { state, offset, patch, clearFilters, setDetail, toggleSort, toApiNumber } =
    usePaymentListUrlState();

  const listParams: SubscriptionListParams = {
    limit: state.limit,
    offset,
    ...(state.q.trim() ? { q: state.q.trim() } : {}),
    ...(state.billing_status
      ? { billing_status: state.billing_status as SubscriptionListParams['billing_status'] }
      : {}),
    ...(state.uid ? { uid: toApiNumber(state.uid) } : {}),
    ...(state.oid ? { oid: toApiNumber(state.oid) } : {}),
    ...(state.eid ? { eid: toApiNumber(state.eid) } : {}),
    ...(state.sort_by ? { sort_by: state.sort_by, order: state.order || 'desc' } : {}),
  };

  const { data, isLoading, isError, error } = useSubscriptionListQuery(
    listParams,
    canView && !permLoading,
  );

  const subscriptions = data?.subscriptions ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <Repeat className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Subscriptions</h1>
          </div>
          <p className="text-sm text-gray-500">Pause, resume, renew, cancel, or uncancel plans.</p>
          <div className="mt-3">
            <ExportCsvButton path="/api/admin/v2/subscriptions" params={listParams} filename="subscriptions.csv" />
          </div>
        </div>

        <PaymentsFilterBar
          state={state}
          onPatch={patch}
          onClear={() => clearFilters()}
          billingStatusOptions={BILLING_STATUS_OPTIONS}
          showAmount={false}
          showDates={false}
          showOid
          showCcode={false}
          searchPlaceholder="Search ffid, uuid, or id…"
        />

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load subscriptions'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    PLID
                  </th>
                  <SortableTh
                    label="Amount"
                    field="amount"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Interval
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    User
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Org
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Entity
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Card
                  </th>
                  <SortableTh
                    label="Expires"
                    field="date_expired"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 9 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-16" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && subscriptions.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-sm text-gray-500">
                      No subscriptions found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  subscriptions.map((sub: Subscription) => (
                    <tr
                      key={sub.plid}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setDetail('plid', sub.plid)}
                    >
                      <td className="px-6 py-4 font-mono text-sm font-semibold">{sub.plid}</td>
                      <td className="px-6 py-4 text-sm font-bold">
                        {formatMoney(sub.amount, sub.ccode)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            STATUS_BADGE[String(sub.billing_status)] || STATUS_BADGE.active
                          }`}
                        >
                          {String(sub.billing_status || '—').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {sub.interval_type || '—'}
                      </td>
                      <td className="max-w-[160px] px-6 py-4">
                        <UserRelationshipLink uid={sub.uid} summary={sub.user_summary} />
                      </td>
                      <td className="max-w-[140px] px-6 py-4">
                        <OrgRelationshipLink oid={sub.oid} summary={sub.org_summary} />
                      </td>
                      <td className="max-w-[160px] px-6 py-4">
                        <EntityRelationshipLink eid={sub.eid} summary={sub.entity_summary} />
                      </td>
                      <td className="px-6 py-4">
                        {sub.bpmid != null ? (
                          <RelationshipLink
                            kind="card"
                            id={sub.bpmid}
                            label={`bpmid ${sub.bpmid}`}
                            extra={{ uid: sub.uid ?? undefined, oid: sub.oid ?? undefined, bpmid: sub.bpmid }}
                          />
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(sub.date_expired)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={state.page}
              itemsPerPage={state.limit}
              limit={state.limit}
              setPage={(page) => patch({ page }, false)}
              setItemsPerPage={(limit) => patch({ limit, page: 1 }, false)}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <SubscriptionDetailsDrawer
        plid={state.plid}
        isOpen={state.plid != null}
        setIsOpen={(open) => {
          if (!open) setDetail('plid', null);
        }}
        canEdit={canEdit}
        canVoid={canVoid}
        onClose={() => setDetail('plid', null)}
      />
    </div>
  );
};

export default SubscriptionListPage;
